import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCalendarTimes,
  faCalendarPlus,
  faFilter,
  faSort,
  faSearch
} from '@fortawesome/free-solid-svg-icons';
import { 
  getBlockedDays, 
  deleteBlockedDay
} from '../../services/blockedDaysService';
import BlockDayForm from './BlockDayForm';
import BlockedDaysList from './BlockedDaysList';

const CalendarBlockingSection = () => {
  const [blockedDays, setBlockedDays] = useState([]);
  const [filteredBlockedDays, setFilteredBlockedDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showBlockForm, setShowBlockForm] = useState(false);
  const [currentBlockedDay, setCurrentBlockedDay] = useState(null);
  const [filterLocation, setFilterLocation] = useState('all');
  const [sortBy, setSortBy] = useState('date-asc');
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch blocked days on component mount
  useEffect(() => {
    fetchBlockedDays();
  }, []);

  const fetchBlockedDays = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getBlockedDays();
      setBlockedDays(data);
    } catch (err) {
      console.error('Error fetching blocked days:', err);
      setError('Failed to load blocked days. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort blocked days when data or filters change
  useEffect(() => {
    const filterAndSortBlockedDays = () => {
    let filtered = [...blockedDays];

    // Filter by location
    if (filterLocation !== 'all') {
      filtered = filtered.filter(day => 
        day.location === filterLocation || day.location === 'All'
      );
    }

    // Filter by search term (reason or date)
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(day =>
        (day.reason || '').toLowerCase().includes(searchLower) ||
        (day.date || '').includes(searchTerm)
      );
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date-asc':
          return new Date(a.date) - new Date(b.date);
        case 'date-desc':
          return new Date(b.date) - new Date(a.date);
        case 'reason-asc':
          return a.reason.localeCompare(b.reason);
        case 'reason-desc':
          return b.reason.localeCompare(a.reason);
        case 'location-asc':
          return a.location.localeCompare(b.location);
        case 'location-desc':
          return b.location.localeCompare(a.location);
        default:
          return new Date(a.date) - new Date(b.date);
      }
    });

    setFilteredBlockedDays(filtered);
    };
    
    filterAndSortBlockedDays();
  }, [blockedDays, filterLocation, sortBy, searchTerm]);

  const handleCreateBlock = () => {
    setCurrentBlockedDay(null);
    setShowBlockForm(true);
  };

  const handleEditBlock = (blockedDay) => {
    setCurrentBlockedDay(blockedDay);
    setShowBlockForm(true);
  };

  const handleDeleteBlock = async (blockedDay) => {
    // The list provides its own inline Yes/No confirmation before calling this.
    try {
      await deleteBlockedDay(blockedDay.id);
      fetchBlockedDays(); // Refresh the list
    } catch (err) {
      console.error('Error deleting blocked day:', err);
      alert('Failed to delete blocked day. Please try again.');
    }
  };

  const handleFormSubmit = () => {
    setShowBlockForm(false);
    fetchBlockedDays(); // Refresh the list
  };

  const handleFormCancel = () => {
    setShowBlockForm(false);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short',
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getUpcomingBlockedDays = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return filteredBlockedDays
      .filter(day => new Date(day.date) >= today)
      .slice(0, 5);
  };

  const getPastBlockedDays = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return filteredBlockedDays.filter(day => new Date(day.date) < today);
  };

  if (loading) {
    return (
      <div className="dashboard-section calendar-blocking-section">
        <div className="section-header">
          <h2>Calendar Blocking</h2>
        </div>
        <div className="loading">Loading blocked days...</div>
      </div>
    );
  }

  return (
    <div className="dashboard-section calendar-blocking-section">
      <div className="section-header">
        <h2>Calendar Blocking</h2>
        <button 
          className="primary-button"
          onClick={handleCreateBlock}
        >
          <FontAwesomeIcon icon={faCalendarPlus} /> Block Calendar Days
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Summary Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{backgroundColor: '#d32f2f'}}>
            <FontAwesomeIcon icon={faCalendarTimes} />
          </div>
          <div className="stat-details">
            <h3>Total Blocked Days</h3>
            <div className="stat-value">{filteredBlockedDays.length}</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{backgroundColor: '#fb8c00'}}>
            <FontAwesomeIcon icon={faCalendarPlus} />
          </div>
          <div className="stat-details">
            <h3>Upcoming Blocked Days</h3>
            <div className="stat-value">{getUpcomingBlockedDays().length}</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{backgroundColor: '#43a047'}}>
            <FontAwesomeIcon icon={faCalendarTimes} />
          </div>
          <div className="stat-details">
            <h3>Past Blocked Days</h3>
            <div className="stat-value">{getPastBlockedDays().length}</div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="filters-bar">
        <div className="search-box">
          <FontAwesomeIcon icon={faSearch} />
          <input
            type="text"
            placeholder="Search by date or reason..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-box">
          <FontAwesomeIcon icon={faFilter} />
          <select
            value={filterLocation}
            onChange={(e) => setFilterLocation(e.target.value)}
          >
            <option value="all">All Locations</option>
            <option value="Angola">Angola Office</option>
            <option value="Elkhart">Elkhart Office</option>
            <option value="All">Both Offices</option>
          </select>
        </div>

        <div className="sort-box">
          <FontAwesomeIcon icon={faSort} />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="date-asc">Date (Oldest First)</option>
            <option value="date-desc">Date (Newest First)</option>
            <option value="reason-asc">Reason (A-Z)</option>
            <option value="reason-desc">Reason (Z-A)</option>
            <option value="location-asc">Location (A-Z)</option>
            <option value="location-desc">Location (Z-A)</option>
          </select>
        </div>
      </div>

      {/* Blocked Days List */}
      <BlockedDaysList
        blockedDays={filteredBlockedDays}
        onEdit={handleEditBlock}
        onDelete={handleDeleteBlock}
        formatDate={formatDate}
      />

      {/* Block Day Form Modal */}
      {showBlockForm && (
        <BlockDayForm
          blockedDay={currentBlockedDay}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
        />
      )}
    </div>
  );
};

export default CalendarBlockingSection;